import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../utils/axios'; // Adjust path

// React Bootstrap components
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert'; // react-bootstrap/Alert
import Spinner from 'react-bootstrap/Spinner';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container'; // Optional: for overall page structure
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

// Lucide icons (can still be used alongside Bootstrap)
import { Users, UserPlus, AlertTriangle as AlertTriangleIcon, CheckCircle as CheckCircleIcon, Trash2, XCircle as XIcon, Edit3, Badge } from 'lucide-react'; // Renamed AlertTriangle and CheckCircle to avoid conflict with Bootstrap Alert

// --- (ORG_MEMBER_ROLES, ADMIN_ROLES remain the same) ---
const ORG_MEMBER_ROLES = [
    { value: 'member', label: 'Member' },
    { value: 'admin', label: 'Admin' },
];
const ADMIN_ROLES = ['Owner', 'Admin', 'owner', 'admin']; // Ensure these match your JWT/backend roles

// Custom Alert component using react-bootstrap's Alert for consistency
const BootstrapAlert = ({ variant, message, onDismiss, show }) => {
  if (!message || !show) return null;
  const Icon = variant === 'danger' ? AlertTriangleIcon : CheckCircleIcon;
  return (
    <Alert variant={variant} onClose={onDismiss} dismissible={!!onDismiss} className="d-flex align-items-center">
      <Icon className={`h-5 w-5 me-2 text-${variant === 'danger' ? 'danger': 'success'}`} />
      {message}
    </Alert>
  );
};


const DashboardPage = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);

  const [isAddingUser, setIsAddingUser] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserRole, setAddUserRole] = useState(ORG_MEMBER_ROLES[0]?.value || 'member');
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState(null);
  const [addUserSuccess, setAddUserSuccess] = useState(null);

  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteUserError, setDeleteUserError] = useState(null);
  const [deleteUserSuccess, setDeleteUserSuccess] = useState(null);

  const [quizScores, setQuizScores] = useState([]);
  const [isLoadingScores, setIsLoadingScores] = useState(true);
  const [scoresError, setScoresError] = useState(null);

  const userIsOrgAdmin = user?.organization_id && user?.organization_role && ADMIN_ROLES.includes(user.organization_role);

  const fetchMyScores = async () => {
    return await axiosInstance.get('/books/my-scores/');
  };

  const fetchOrganizationUsers = useCallback(async () => {
    if (!userIsOrgAdmin || !user.organization_id) {
      setOrganizationUsers([]);
      return;
    }
    setIsLoadingUsers(true);
    setUsersError(null);
    try {
      const response = await axiosInstance.get(`/accounts/organizations/${user.organization_id}/users/`);
      setOrganizationUsers(response.data.results || []);
    } catch (error) {
      console.error("Failed to fetch organization users:", error);
      setUsersError(error.response?.data?.detail || "Failed to fetch organization users.");
      setOrganizationUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [user?.organization_id, userIsOrgAdmin]);

  const loadUserScores = useCallback(async () => {
      // ... sets loading to true ...
      try {
          const response = await fetchMyScores(); // <<< This is where your selected code is used
          setQuizScores(response.data.results || response.data || []);
      } catch (error) {
          setScoresError("Could not load your quiz history.");
      } finally {
          setIsLoadingScores(false);
      }
  }, [isAuthenticated]);

  useEffect(() => {
      if (isAuthenticated) {
          loadUserScores();
          // ... also loads organization users if admin ...
      }
  }, [isAuthenticated, userIsOrgAdmin, loadUserScores]);

  useEffect(() => {
    if (isAuthenticated && userIsOrgAdmin) {
      fetchOrganizationUsers();
    }
  }, [isAuthenticated, userIsOrgAdmin, fetchOrganizationUsers]);

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    if (!userIsOrgAdmin || !user.organization_id) return;

    setAddUserLoading(true);
    setAddUserError(null);
    setAddUserSuccess(null);
    setDeleteUserError(null);
    setDeleteUserSuccess(null);
    try {
      const response = await axiosInstance.post(`/accounts/organizations/${user.organization_id}/add-member/`, {
        email: addUserEmail,
        role: addUserRole,
      });
      setAddUserSuccess(`User ${response.data?.user?.email || addUserEmail} added successfully as ${response.data?.role || addUserRole}.`);
      setAddUserEmail('');
      fetchOrganizationUsers();
    } catch (error) {
      console.error("Failed to add user:", error);
      setAddUserError(error.response?.data?.detail || error.response?.data?.email?.[0] || error.response?.data[Object.keys(error.response?.data || {})[0]]?.[0] || "Failed to add user.");
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleRemoveUser = async (orgMemberIdToRemove, userEmailToRemove) => {
    if (!userIsOrgAdmin || !user.organization_id) return;
    if (!window.confirm(`Are you sure you want to remove ${userEmailToRemove} from the organization?`)) return;

    setDeletingUserId(orgMemberIdToRemove);
    setDeleteUserError(null);
    setDeleteUserSuccess(null);
    setAddUserError(null);
    setAddUserSuccess(null);
    try {
      await axiosInstance.delete(`/accounts/organizations/${user.organization_id}/members/${orgMemberIdToRemove}/`);
      setDeleteUserSuccess(`User ${userEmailToRemove} removed successfully.`);
      fetchOrganizationUsers();
    } catch (error) {
      console.error("Failed to remove user:", error);
      setDeleteUserError(error.response?.data?.detail || `Failed to remove user ${userEmailToRemove}.`);
    } finally {
      setDeletingUserId(null);
    }
  };

  if (!isAuthenticated) {
    return <p className="text-center mt-5">Please log in to view the dashboard.</p>;
  }

  // Using Tailwind for overall page layout, react-bootstrap for specific sections
  return (
    <div className="container mt-5 mx-auto max-w-6xl py-8 px-4 sm:px-6 lg:px-8">
      <Card className="shadow-xl mb-4">
        <Card.Body>
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <Users className="h-12 w-12 md:h-16 md:w-16 text-primary mr-0 sm:mr-4 mb-3 sm:mb-0 flex-shrink-0" />
            <div className="flex-grow">
              <Card.Title as="h1" className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">Dashboard</Card.Title>
              <Card.Text className="text-md md:text-lg text-secondary">Welcome, {user?.full_name || user?.email} !</Card.Text>
              {user?.organization_id && (
                <Card.Text className="text-sm text-muted mt-1">
                  Organization: <span className="font-weight-medium text-dark">{user.organization_name || `ID ${user.organization_id}`}</span> | Your Role: <span className="font-weight-medium text-dark">{user.organization_role}</span>
                </Card.Text>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {userIsOrgAdmin && user.organization_id && (
        <Card className="mt-4 shadow-lg">
          <Card.Header className="bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-0">
                Organization Members
                <span className="text-muted ms-2 fs-6">({user.organization_name || `ID ${user.organization_id}`})</span>
              </h2>
              <Button
                variant={isAddingUser ? "outline-secondary" : "success"}
                size="sm"
                onClick={() => setIsAddingUser(!isAddingUser)}
              >
                <UserPlus size={18} className="me-2" /> {isAddingUser ? 'Cancel Adding' : 'Add User'}
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {isAddingUser && (
              <Card className="mb-4 shadow-sm bg-lightest"> {/* Using a custom class or inline style might be needed for bg-lightest */}
                <Card.Body>
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Add New User to Organization</h3>
                  <Form onSubmit={handleAddUserSubmit}>
                    <BootstrapAlert variant="danger" message={addUserError} show={!!addUserError} onDismiss={() => setAddUserError(null)} />
                    <BootstrapAlert variant="success" message={addUserSuccess} show={!!addUserSuccess} onDismiss={() => setAddUserSuccess(null)} />

                    <Form.Group as={Row} className="mb-3" controlId="addUserEmailForm">
                      <Form.Label column sm="3" className="text-sm font-medium text-gray-700">
                        User's Email:
                      </Form.Label>
                      <Col sm="9">
                        <Form.Control
                          type="email"
                          value={addUserEmail}
                          onChange={(e) => setAddUserEmail(e.target.value)}
                          required
                          placeholder="user@example.com"
                          size="sm"
                        />
                      </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3" controlId="addUserRoleForm">
                      <Form.Label column sm="3" className="text-sm font-medium text-gray-700">
                        Assign Role:
                      </Form.Label>
                      <Col sm="9">
                        <Form.Select
                          value={addUserRole}
                          onChange={(e) => setAddUserRole(e.target.value)}
                          required
                          size="sm"
                        >
                          {ORG_MEMBER_ROLES.map(role => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                          ))}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                    
                    <Form.Group as={Row}>
                        <Col sm={{ span: 9, offset: 3 }}>
                            <Button
                                variant="success"
                                type="submit"
                                disabled={addUserLoading}
                                size="sm"
                                className="d-flex align-items-center"
                            >
                                {addUserLoading ? (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                ) : (
                                <UserPlus size={18} className="me-2" />
                                )}
                                Add User
                            </Button>
                        </Col>
                    </Form.Group>
                  </Form>
                </Card.Body>
              </Card>
            )}

            <BootstrapAlert variant="danger" message={deleteUserError} show={!!deleteUserError} onDismiss={() => setDeleteUserError(null)} />
            <BootstrapAlert variant="success" message={deleteUserSuccess} show={!!deleteUserSuccess} onDismiss={() => setDeleteUserSuccess(null)} />
            
            <h4 className="text-md font-medium text-gray-700 mb-3 mt-2">Current Members:</h4>
            {isLoadingUsers ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" className="me-2" />
                <span className="text-secondary">Loading users...</span>
              </div>
            ) : usersError ? (
              <BootstrapAlert variant="danger" message={usersError} show={!!usersError} onDismiss={() => setUsersError(null)} />
            ) : organizationUsers.length === 0 ? (
              <p className="text-secondary fst-italic">No users found in this organization.</p>
            ) : (
              <Table striped bordered hover responsive size="sm" className="mt-2 shadow-sm rounded overflow-hidden">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {organizationUsers.map((orgUser, index) => (
                    <tr key={orgUser.id}>
                      <td>{index + 1}</td>
                      <td>{orgUser.full_name}</td>
                      <td>{orgUser.email}</td>
                      <td>
                        <span className={`badge rounded-pill text-dark ${
                          ADMIN_ROLES.includes(orgUser.role_in_organization) ? 'bg-primary-subtle' : 'bg-success-subtle'
                        }`}>
                          {orgUser.role_in_organization}
                        </span>
                      </td>
                      <td>
                        {user.user_id !== orgUser.id ? (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveUser(orgUser.organization_membership_id, orgUser.email)}
                            disabled={deletingUserId === orgUser.organization_membership_id}
                            title={`Remove ${orgUser.full_name}`}
                            className="p-1 d-flex align-items-center justify-content-center"
                            style={{ lineHeight: 1, width: '30px', height: '30px' }}
                          >
                            {deletingUserId === orgUser.organization_membership_id ? (
                              <Spinner as="span" animation="border" size="sm" />
                            ) : (
                              <XIcon size={16} />
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted fst-italic text-xs">Yourself</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      <Card className="shadow-lg mb-4">
        <Card.Header as="h2">
            My Quiz History
        </Card.Header>
        <Card.Body>
            {isLoadingScores ? (
                <Spinner /> // Shows loading spinner
            ) : scoresError ? (
                <Alert variant="danger">{scoresError}</Alert> // Shows error
            ) : quizScores.length === 0 ? (
                <Alert variant="info">You have not taken any quizzes yet.</Alert> // Shows empty state
            ) : (
                <Table striped bordered hover responsive size="sm" className="mt-2 shadow-sm rounded overflow-hidden">
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Quiz Title</th>
                                    <th>Score</th>
                                    <th>Percentage</th>
                                    <th>Date Submitted</th>
                                </tr>
                            </thead>
                            {/* --- FIX: Added <tbody> wrapper --- */}
                            <tbody>
                                {quizScores.map((score, index) => (
                                    <tr key={score.id}>
                                        <td>{index + 1}</td>
                                        <td>{score.quiz_title}</td>
                                        <td>{score.correct_answers} / {score.total_questions}</td>
                                        <td>
                                            {score.score_percentage.toFixed(1)}%
                                        </td>
                                        <td>{new Date(score.submitted_at).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
            )}
        </Card.Body>
    </Card>

      {/* ... (Messages for non-admin members or users not in an org, as before, can also be styled with Bootstrap Alerts) ... */}
      {!userIsOrgAdmin && user?.organization_id && (
          <Alert variant="warning" className="mt-4">
            <AlertTriangleIcon className="inline h-5 w-5 me-2" />
            You are a member of an organization but do not have admin rights to manage users.
          </Alert>
      )}
      {!user?.organization_id && (
          <Alert variant="info" className="mt-4">
            You are not currently associated with any organization.
          </Alert>
      )}
    </div>
  );
};

export default DashboardPage;